import { IGroundRaster } from './labelGroundTone'
import {
  IRasterLayerDescriptor,
  IUnreadableGround,
} from './rasterGroundSources'
import {
  boundsCoverTileAt,
  coveringZoom,
  IPixelRect,
  mercatorX,
  mercatorY,
  probeRect,
  quadToUnitSquare,
  tileAt,
} from './rasterTileMath'

// Resolves what each label is standing on, reading source assets rather than the rendered
// frame. Pure state machine over an injected runtime — the mapbox/DOM half lives in
// rasterGroundRuntime and contains no decisions, which is what lets all of this test in node.

export interface IRasterProbe {
  key: string
  lng: number
  lat: number
  // One cell of the label's own footprint, in screen px. Sizing the probe to the text is the
  // correct version of what the canvas sampler got wrong: it failed because the FRAME held
  // the label's casing, and a source tile holds none.
  widthPx: number
  heightPx: number
}

export interface IRasterRuntime {
  describeLayers(mapZoom: number): {
    descriptors: IRasterLayerDescriptor[]
    unreadable: IUnreadableGround[]
  }
  // Synchronous: an image source's bitmap is already decoded and resident in the process.
  readImage(sourceId: string, rect: IPixelRect): IGroundRaster | null
  // One decode, many samples. The decode is the whole cost of a read — a 256px tile is 65k
  // pixels — so keying work by rect instead of by tile meant decoding the same tile up to
  // three times to read 225 pixels out of it. Rects are in tileSize-px space; a retina tile
  // decodes larger and the runtime rescales. Resolves null when the tile is absent (a 404
  // outside an overlay's footprint is the normal answer), throws when the read fails.
  readTilePatches(
    url: string,
    rects: IPixelRect[],
    tileSize: number,
  ): Promise<Array<IGroundRaster | null> | null>
  // Appends the access token and resolves mapbox:// forms; null when unavailable.
  normaliseTileUrl(
    template: string,
    z: number,
    x: number,
    y: number,
    tileSize: number,
  ): string | null
  report(event: IRasterGroundReport): void
}

export interface IRasterGroundReport {
  kind: 'unreadable' | 'source-disabled'
  sourceId: string
  detail: string
}

export interface IGroundReadout {
  // Complete stacks, bottom-up. A key present with an empty array means "nothing covers this
  // point" — a real answer, not a missing one.
  rasters: Map<string, IGroundRaster[]>
  // Covered by a layer whose pixels are not in hand yet. The caller must NOT tone these from
  // the partial stack: doing so paints the basemap's answer for a frame and then corrects
  // it, which is a visible blink on every arrival at new ground.
  pending: Set<string>
}

export interface IRasterGroundReader {
  // Everything answerable from memory right now. Never fetches, never throws, never awaits.
  peek(probes: IRasterProbe[], mapZoom: number): IGroundReadout
  // Reads whatever peek could not answer, then returns the complete stack. Superseding is the
  // CALLER's job, via its own generation token: one reader is shared by every label layer on a
  // map, so a cancellation here would cancel a read another layer is waiting on. Reads are
  // capped, memoised and short, which makes cancelling them worth less than that hazard.
  resolve(probes: IRasterProbe[], mapZoom: number): Promise<IGroundReadout>
}

// Tiles are decoded, sampled and discarded; what is retained is the SAMPLE, a handful of
// numbers per key rather than a quarter-megabyte tile.
//
// It has to hold at least one settle's working set — keys are per (probe x layer), so a pass
// wants a few thousand. Below that the cache cannot warm at all and every settle re-fetches
// what the last one paid for. The trim is deferred to the end of a pass, so a dense settle can
// transiently exceed this.
export const MEMO_LIMIT = 4096
// Quantising the probe centre makes a small pan a cache hit, which also buys tone stability
// across tiny camera moves for free.
const CELL_PX = 8
const MAX_SOURCE_FAILURES = 3
// How long a written-off source stays written off. Long enough that a dead one is not hammered
// — three attempts a minute at worst — short enough that a blip during load does not cost the
// session.
const SOURCE_DISABLE_MS = 60_000
const TRANSPARENT: IGroundRaster = { rgb: [0, 0, 0], alpha: 0 }

function cell(v: number): number {
  return Math.floor(v / CELL_PX)
}

// Everything about a layer that does not depend on which cell is asking. Recomputing this
// per cell is what made a screenful of labels expensive: the image quad's inverse is a 3x3
// solve, and a tile URL is three regex replaces plus the mapbox normaliser.
interface IPreparedLayer {
  descriptor: IRasterLayerDescriptor
  toUv?: (x: number, y: number) => [number, number]
  imageWidth?: number
  imageHeight?: number
  imagePxPerScreenPx?: number
  z?: number
  tileSize?: number
  urlByTile?: Map<string, string | null>
}

function scaleAlpha(sample: IGroundRaster, opacity: number): IGroundRaster {
  return opacity >= 1 ? sample : { ...sample, alpha: sample.alpha * opacity }
}

export function createRasterGroundReader(
  runtime: IRasterRuntime,
): IRasterGroundReader {
  // Insertion-ordered, so the oldest key is the first key.
  const memo = new Map<string, IGroundRaster>()
  const inFlight = new Map<string, Promise<IGroundRaster>>()
  const failures = new Map<string, number>()
  // Written off UNTIL a moment, not forever. Date.now rather than performance.now so a test
  // can move the clock.
  const disabledUntil = new Map<string, number>()
  const reported = new Set<string>()

  const remember = (key: string, value: IGroundRaster): void => {
    if (memo.has(key)) memo.delete(key)
    memo.set(key, value)
  }

  // Deferred to the END of a pass rather than done per write, and that is correctness, not
  // tidiness: resolve() writes every sample through remember() and only then reads them back
  // through peek(). Trimming inline let a pass evict its OWN entries before that read, and a
  // missing key reads as `pending` — which keeps the prior tone, with nothing re-running on a
  // still camera. Deferring means the pass always reads what it wrote.
  const trimMemo = (): void => {
    while (memo.size > MEMO_LIMIT) {
      memo.delete(memo.keys().next().value as string)
    }
  }

  const recall = (key: string): IGroundRaster | undefined => {
    const hit = memo.get(key)
    if (hit) {
      memo.delete(key)
      memo.set(key, hit)
    }
    return hit
  }

  const reportOnce = (event: IRasterGroundReport): void => {
    const id = `${event.kind}|${event.sourceId}|${event.detail}`
    if (reported.has(id)) return
    reported.add(id)
    runtime.report(event)
  }

  // What to read for one probe against one layer: a memo key, plus the rect to sample.
  // Returns null when the layer does not cover this point at all.
  const prepare = (
    descriptors: IRasterLayerDescriptor[],
    mapZoom: number,
  ): IPreparedLayer[] => {
    const prepared: IPreparedLayer[] = []
    const mercatorPerScreenPx = 1 / (512 * Math.pow(2, mapZoom))
    for (const descriptor of descriptors) {
      if (descriptor.kind === 'image') {
        const corners = descriptor.coordinates
        const imageWidth = descriptor.imageWidth ?? 0
        const imageHeight = descriptor.imageHeight ?? 0
        if (!corners || corners.length !== 4 || !imageWidth || !imageHeight)
          continue
        const toUv = quadToUnitSquare([
          [mercatorX(corners[0][0]), mercatorY(corners[0][1])],
          [mercatorX(corners[1][0]), mercatorY(corners[1][1])],
          [mercatorX(corners[2][0]), mercatorY(corners[2][1])],
          [mercatorX(corners[3][0]), mercatorY(corners[3][1])],
        ])
        if (!toUv) continue
        const mercWidth = Math.max(
          1e-12,
          Math.abs(mercatorX(corners[1][0]) - mercatorX(corners[0][0])),
        )
        prepared.push({
          descriptor,
          toUv,
          imageWidth,
          imageHeight,
          imagePxPerScreenPx: (imageWidth / mercWidth) * mercatorPerScreenPx,
        })
        continue
      }
      const tileSize = descriptor.tileSize ?? 512
      const z = coveringZoom(
        mapZoom,
        tileSize,
        descriptor.minzoom ?? 0,
        descriptor.maxzoom ?? 22,
      )
      if (z == null) continue
      prepared.push({ descriptor, z, tileSize, urlByTile: new Map() })
    }
    return prepared
  }

  const plan = (
    layer: IPreparedLayer,
    probe: IRasterProbe,
    mapZoom: number,
  ): {
    key: string
    rect: IPixelRect
    url?: string
    tileSize: number
  } | null => {
    const d = layer.descriptor
    if (d.kind === 'image') {
      const w = layer.imageWidth as number
      const h = layer.imageHeight as number
      const [u, v] = (layer.toUv as (x: number, y: number) => [number, number])(
        mercatorX(probe.lng),
        mercatorY(probe.lat),
      )
      // Outside the sheet: this layer is not the ground here.
      if (!(u >= 0 && u <= 1 && v >= 0 && v <= 1)) return null

      const perScreenPx = layer.imagePxPerScreenPx as number
      const rw = Math.max(
        1,
        Math.min(Math.round(probe.widthPx * perScreenPx), w),
      )
      const rh = Math.max(
        1,
        Math.min(Math.round(probe.heightPx * perScreenPx), h),
      )
      const px = u * w
      const py = v * h
      const x = Math.min(Math.max(0, Math.round(px - rw / 2)), w - rw)
      const y = Math.min(Math.max(0, Math.round(py - rh / 2)), h - rh)
      return {
        key: `${d.sourceId}|img|${cell(px)},${cell(py)}|${rw}x${rh}`,
        rect: { x, y, width: rw, height: rh },
        tileSize: w,
      }
    }

    const tileSize = layer.tileSize as number
    const z = layer.z as number
    // Outside the footprint: not the ground here, as in the image branch above.
    if (d.bounds && !boundsCoverTileAt(d.bounds, probe.lng, probe.lat, z)) {
      return null
    }

    const at = tileAt(probe.lng, probe.lat, z, d.scheme ?? 'xyz')
    const rect = probeRect(
      at.fx,
      at.fy,
      tileSize,
      probe.widthPx,
      probe.heightPx,
      z,
      mapZoom,
    )
    // Always normalised, never only for mapbox:// forms. A raster tileset mounted by
    // TileJSON url resolves to absolute api.mapbox.com templates that still 401 without the
    // access token, and the normaliser is a no-op on a URL that is not mapbox's. Cached per
    // tile, since every cell on a tile resolves to the same address.
    const tileKey = `${at.x}/${at.y}`
    const cachedUrls = layer.urlByTile as Map<string, string | null>
    let url = cachedUrls.get(tileKey)
    if (url === undefined) {
      url = runtime.normaliseTileUrl(
        d.tileTemplate ?? '',
        z,
        at.x,
        at.y,
        tileSize,
      )
      cachedUrls.set(tileKey, url)
    }
    if (!url) {
      reportOnce({
        kind: 'unreadable',
        sourceId: d.sourceId,
        detail: 'no-tile-url',
      })
      return null
    }
    return {
      key: `${d.sourceId}|${z}/${at.x}/${at.y}|${cell(rect.x)},${cell(rect.y)}`,
      rect,
      url,
      tileSize,
    }
  }

  const layersFor = (
    mapZoom: number,
  ): { descriptors: IPreparedLayer[]; loading: boolean } => {
    // Three failures write a source off, and without a way back a transient blip costs every
    // label on that imagery its ground for the session, with nothing retrying.
    //
    // The way back is a COOLDOWN, not an event. `styledata` looks like the natural signal and is
    // not: it fires for setLayoutProperty and setPaintProperty too, both of which this app
    // drives at interactive rates, so a reset keyed on it would hammer a genuinely dead source.
    const now = Date.now()
    for (const [sourceId, until] of disabledUntil) {
      if (now < until) continue
      disabledUntil.delete(sourceId)
      failures.delete(sourceId)
      // So a source that fails again still says so, rather than being written off in silence.
      // The dedup id carries the attempt count in its detail, so the prefix is what matches.
      const prefix = `source-disabled|${sourceId}|`
      for (const id of reported) if (id.startsWith(prefix)) reported.delete(id)
    }
    const { descriptors, unreadable } = runtime.describeLayers(mapZoom)
    let loading = false
    for (const u of unreadable) {
      // A source whose TileJSON is still in flight has no footprint yet, so we cannot even
      // say which labels it covers. Treating that as "nothing covers them" is what made a
      // label settle on the basemap's answer and then keep it: the answer looked complete,
      // so it seeded the sticky tone, and the imagery arriving later could not overturn it.
      if (u.reason === 'source-not-loaded') {
        loading = true
        continue
      }
      // The rest mean something is actually wrong.
      if (u.reason === 'unsupported-source' || u.reason === 'source-missing') {
        reportOnce({
          kind: 'unreadable',
          sourceId: u.sourceId,
          detail: `${u.reason}:${u.sourceType}`,
        })
      }
    }
    return {
      descriptors: prepare(
        descriptors.filter(d => !disabledUntil.has(d.sourceId)),
        mapZoom,
      ),
      loading,
    }
  }

  const noteFailure = (sourceId: string): void => {
    const n = (failures.get(sourceId) ?? 0) + 1
    failures.set(sourceId, n)
    if (n >= MAX_SOURCE_FAILURES) {
      disabledUntil.set(sourceId, Date.now() + SOURCE_DISABLE_MS)
      reportOnce({
        kind: 'source-disabled',
        sourceId,
        detail: `${n} consecutive read failures`,
      })
    }
  }

  return {
    peek(probes, mapZoom) {
      const rasters = new Map<string, IGroundRaster[]>()
      const pending = new Set<string>()
      const { descriptors, loading } = layersFor(mapZoom)
      for (const probe of probes) {
        // Footprint unknown while a source loads, so every label is potentially on it.
        if (loading) pending.add(probe.key)
        const stack: IGroundRaster[] = []
        for (const layer of descriptors) {
          const d = layer.descriptor
          const p = plan(layer, probe, mapZoom)
          // Not covered by this layer: it is not this label's ground, nothing to wait for.
          if (!p) continue
          if (d.kind === 'image') {
            const sample =
              recall(p.key) ??
              runtime.readImage(d.sourceId, p.rect) ??
              undefined
            if (sample) {
              remember(p.key, sample)
              stack.push(scaleAlpha(sample, d.opacity))
            } else {
              pending.add(probe.key)
            }
            continue
          }
          const hit = recall(p.key)
          if (hit) {
            stack.push(scaleAlpha(hit, d.opacity))
          } else {
            pending.add(probe.key)
          }
        }
        rasters.set(probe.key, stack)
      }
      trimMemo()
      return { rasters, pending }
    },

    async resolve(probes, mapZoom) {
      const { descriptors } = layersFor(mapZoom)
      const reads: Array<Promise<void>> = []
      const resolved = new Map<string, IGroundRaster>()
      // Every rect that has to come off the same tile, gathered before a single byte is read.
      const batches = new Map<
        string,
        {
          sourceId: string
          tileSize: number
          items: Array<{ key: string; rect: IPixelRect }>
          // Membership only. Zoomed out, one tile can cover the whole viewport, so every
          // probe lands in the same batch and the dedup below was a linear scan per insert —
          // quadratic in a batch that reaches a few thousand items.
          seen: Set<string>
        }
      >()

      for (const probe of probes) {
        for (const layer of descriptors) {
          const d = layer.descriptor
          if (d.kind !== 'tiles') continue
          const p = plan(layer, probe, mapZoom)
          if (!p || !p.url) continue
          if (recall(p.key) || resolved.has(p.key)) continue

          const running = inFlight.get(p.key)
          if (running) {
            reads.push(
              running.then(value => {
                resolved.set(p.key, value)
              }),
            )
            continue
          }

          let batch = batches.get(p.url)
          if (!batch) {
            batch = {
              sourceId: d.sourceId,
              tileSize: p.tileSize,
              items: [],
              seen: new Set(),
            }
            batches.set(p.url, batch)
          }
          if (!batch.seen.has(p.key)) {
            batch.seen.add(p.key)
            batch.items.push({ key: p.key, rect: p.rect })
          }
        }
      }

      for (const [url, batch] of batches) {
        const job = runtime
          .readTilePatches(
            url,
            batch.items.map(item => item.rect),
            batch.tileSize,
          )
          .then(samples => {
            // A 404 is a completed exchange, not a fault: it is the normal answer outside an
            // orthomosaic's footprint, and memoising it stops it being asked every settle.
            failures.delete(batch.sourceId)
            return samples
          })
          .catch(() => {
            noteFailure(batch.sourceId)
            return null
          })

        batch.items.forEach((item, index) => {
          const perKey = job
            .then(samples => (samples && samples[index]) || TRANSPARENT)
            .finally(() => {
              inFlight.delete(item.key)
            })
          inFlight.set(item.key, perKey)
          reads.push(
            perKey.then(value => {
              resolved.set(item.key, value)
              // Bytes already paid for are kept even if this settle is superseded.
              remember(item.key, value)
            }),
          )
        })
      }

      await Promise.all(reads)
      return this.peek(probes, mapZoom)
    },
  }
}
